import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const WatchClassPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classData, setClassData] = useState(null);
  const [relatedClasses, setRelatedClasses] = useState([]);
  const [activeTab, setActiveTab] = useState("notes");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPlaybackOptions, setShowPlaybackOptions] = useState(false);
  const [showNotes, setShowNotes] = useState(true);

  const videoRef = useRef(null);

  // Mock data for demonstration
  const mockClass = {
    id: 1,
    title: "Nagaland State Formation",
    instructor: "Dr. Imtisunep Longchar",
    date: "March 10, 2025",
    subject: "History",
    type: "foundation", // foundation, prelims, mains
    description:
      "Detailed analysis of the events leading to the formation of Nagaland state, including the role of Naga National Council and the 16-Point Agreement.",
    videoUrl:
      "http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", // In real implementation, this would be your video source
    duration: "2h 15m",
    thumbnail:
      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSZmWumNV-QvHdGvIpWoP2_ZuY6tjxbsMeShQ&s",
    popular: true,
    rating: 4.8,
    views: 342,
    lastWatched: "00:45:20", // Format: hh:mm:ss
    completion: 35, // Percentage of completion
    bookmarks: [
      { time: "00:10:25", label: "Independence movement begins" },
      { time: "00:32:15", label: "Naga National Council formation" },
      { time: "01:15:30", label: "16-Point Agreement" },
      { time: "01:45:20", label: "State formation declaration" },
    ],
    notes: [
      {
        title: "Introduction",
        content:
          "This lesson covers the historical events that led to the formation of Nagaland as a state. We'll examine the pre-independence situation, the role of key leaders, the movements that shaped the demand for statehood, and the political negotiations that eventually resulted in the creation of Nagaland.",
        timestamp: "00:00:00",
      },
      {
        title: "Historical Background",
        content:
          "Before the British colonization, Naga tribes lived in autonomous village republics. The British annexed Naga Hills in 1881, bringing it under colonial administration. This section explores how colonial contact influenced later political developments.",
        timestamp: "00:05:30",
      },
      {
        title: "Independence Movement",
        content:
          "The Naga independence movement began in earnest with the formation of the Naga Club in 1918. It gained momentum with the leadership of A.Z. Phizo and the establishment of the Naga National Council (NNC) in 1946. The NNC declared Naga independence on August 14, 1947, one day before Indian independence.",
        timestamp: "00:10:25",
      },
      {
        title: "The Naga Plebiscite",
        content:
          "The NNC conducted a plebiscite in 1951, claiming that 99.9% of Nagas supported independence. The Indian government rejected the validity of this plebiscite. This disagreement laid the groundwork for decades of political conflict.",
        timestamp: "00:25:40",
      },
      {
        title: "Armed Struggle Period",
        content:
          "From the early 1950s, the situation deteriorated into armed conflict. The Indian government deployed military forces to suppress the Naga independence movement. The Armed Forces Special Powers Act (AFSPA) was implemented in the region in 1958.",
        timestamp: "00:40:15",
      },
      {
        title: "The 16-Point Agreement",
        content:
          "A turning point came with the 16-Point Agreement of 1960, negotiated between the Government of India and the Naga People's Convention. This agreement led to the creation of Nagaland as the 16th state of India in 1963. Key provisions included: recognition of the Naga flag, protection of Naga customary laws, and special provisions for Naga autonomy.",
        timestamp: "01:15:30",
      },
      {
        title: "State Formation",
        content:
          "Nagaland was formally inaugurated as a state on December 1, 1963. P. Shilu Ao became the first Chief Minister. The state was formed with special provisions under Article 371A of the Indian Constitution, which protects Naga customary laws, procedures, and land ownership.",
        timestamp: "01:45:20",
      },
      {
        title: "Critical Analysis",
        content:
          "While state formation was a significant milestone, it did not resolve all issues. The more radical factions of the Naga movement rejected the 16-Point Agreement and continued to demand full independence. This section analyzes the limitations of the agreement and why it failed to bring complete peace.",
        timestamp: "01:55:10",
      },
      {
        title: "Conclusion & NPSC Examination Perspective",
        content:
          "Understanding Nagaland's state formation is crucial for NPSC exams. Questions often focus on the constitutional provisions, key leaders, significant dates, and the political processes involved. The interplay between central government policies and local aspirations is a recurring theme in the examination.",
        timestamp: "02:10:00",
      },
    ],
    resources: [
      {
        name: "Class Notes PDF",
        type: "pdf",
        size: "1.4 MB",
        url: "/resources/nagaland-formation-notes.pdf",
      },
      {
        name: "Timeline Chart",
        type: "image",
        size: "850 KB",
        url: "/resources/nagaland-timeline.jpg",
      },
      {
        name: "Important Dates Flashcards",
        type: "pdf",
        size: "520 KB",
        url: "/resources/important-dates.pdf",
      },
      {
        name: "16-Point Agreement Text",
        type: "pdf",
        size: "780 KB",
        url: "/resources/16-point-agreement.pdf",
      },
      {
        name: "Practice Questions",
        type: "pdf",
        size: "650 KB",
        url: "/resources/practice-questions.pdf",
      },
    ],
    chapters: [
      { title: "Introduction", timestamp: "00:00:00" },
      { title: "Historical Background", timestamp: "00:05:30" },
      { title: "Independence Movement", timestamp: "00:10:25" },
      { title: "The Naga Plebiscite", timestamp: "00:25:40" },
      { title: "Armed Struggle Period", timestamp: "00:40:15" },
      { title: "Peace Initiatives", timestamp: "00:55:30" },
      { title: "The 16-Point Agreement", timestamp: "01:15:30" },
      { title: "State Formation", timestamp: "01:45:20" },
      { title: "Critical Analysis", timestamp: "01:55:10" },
      { title: "Conclusion & NPSC Perspective", timestamp: "02:10:00" },
    ],
    quiz: [
      {
        question: "When was Nagaland formally inaugurated as a state?",
        options: [
          "August 14, 1947",
          "January 26, 1950",
          "December 1, 1963",
          "February 20, 1987",
        ],
        correctAnswer: 2,
      },
      {
        question: "Who became the first Chief Minister of Nagaland?",
        options: ["A.Z. Phizo", "P. Shilu Ao", "T.N. Angami", "S.C. Jamir"],
        correctAnswer: 1,
      },
      {
        question:
          "Which article of the Indian Constitution provides special provisions for Nagaland?",
        options: ["Article 356", "Article 370", "Article 371A", "Article 375"],
        correctAnswer: 2,
      },
      {
        question: "In which year was the 16-Point Agreement signed?",
        options: ["1947", "1955", "1960", "1972"],
        correctAnswer: 2,
      },
      {
        question: "Which organization conducted the Naga Plebiscite in 1951?",
        options: [
          "Naga Club",
          "Naga National Council (NNC)",
          "Naga People's Convention",
          "National Socialist Council of Nagaland",
        ],
        correctAnswer: 1,
      },
    ],
  };

  // Mock data for related classes
  const mockRelatedClasses = [
    {
      id: 5,
      title: "Colonial Nagaland: British Period",
      instructor: "Dr. Imtisunep Longchar",
      duration: "1h 45m",
      image: "/images/colonial-history.jpg",
      subject: "History",
      type: "foundation",
    },
    {
      id: 12,
      title: "Naga Customary Laws & Practices",
      instructor: "Prof. Kezhokhoto Savi",
      duration: "2h 10m",
      image: "/images/customary-laws.jpg",
      subject: "Law & Administration",
      type: "prelims",
    },
    {
      id: 18,
      title: "Historical Evolution of Nagaland's Districts",
      instructor: "Dr. Rosemary Dzüvichü",
      duration: "1h 30m",
      image: "/images/districts-history.jpg",
      subject: "Geography & Administration",
      type: "foundation",
    },
  ];

  useEffect(() => {
    // Simulate API fetch
    window.scrollTo(0, 0);

    // In a real implementation, you would fetch the specific class data based on the id
    setTimeout(() => {
      setClassData(mockClass);
      setRelatedClasses(mockRelatedClasses);
      setLoading(false);
    }, 800);
  }, [id]);

  // Handler for seeking to specific timestamp
  const seekToTimestamp = (timestamp) => {
    if (videoRef.current) {
      const [hours, minutes, seconds] = timestamp.split(":").map(Number);
      const timeInSeconds = hours * 3600 + minutes * 60 + seconds;
      videoRef.current.currentTime = timeInSeconds;
      setCurrentTime(timeInSeconds);
      if (!isPlaying) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Format time from seconds to hh:mm:ss
  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Video player controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleDurationChange = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
    setShowPlaybackOptions(false);
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="pt-20 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600">Loading class video...</p>
        </div>
      </div>
    );
  }

  // Error state if class not found
  if (!classData) {
    return (
      <div className="pt-20 flex justify-center items-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
          <svg
            className="h-16 w-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Class Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn't find the class you're looking for. It may have been
            moved or deleted.
          </p>
          <Link
            to="/recorded-classes"
            className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors duration-300"
          >
            Return to Classes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 bg-gray-100 min-h-screen">
      {/* Class Header Bar */}
      <div className="bg-gray-900 text-white py-3 px-4 sm:px-6 lg:px-8 sticky top-16 z-10 mt-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-800 rounded-full"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold truncate max-w-md">
                {classData.title}
              </h1>
              <div className="flex items-center text-sm text-gray-400">
                <span>{classData.subject}</span>
                <span className="mx-2">•</span>
                <span>{classData.instructor}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="p-2 hover:bg-gray-800 rounded-full"
              title="Add to Favorites"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </button>
            <button
              className="p-2 hover:bg-gray-800 rounded-full"
              title="Share"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
            </button>
            <button
              className="hidden md:flex items-center space-x-1 bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded text-sm font-medium"
              onClick={() => setShowNotes(!showNotes)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>{showNotes ? "Hide Notes" : "Show Notes"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Title (visible on small screens) */}
      <div className="sm:hidden bg-gray-800 text-white py-2 px-4">
        <h1 className="text-lg font-semibold">{classData.title}</h1>
        <div className="flex items-center text-sm text-gray-400">
          <span>{classData.subject}</span>
          <span className="mx-2">•</span>
          <span>{classData.instructor}</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row">
          {/* Video Player Area */}
          <div
            className={`${
              showNotes ? "lg:w-3/5" : "w-full"
            } transition-all duration-300`}
          >
            <div className="bg-black rounded-lg overflow-hidden shadow-lg relative">
              {/* Video Element - In a real implementation, you would use a video library or player component */}
              <video
                ref={videoRef}
                className="w-full aspect-video"
                poster={classData.thumbnail}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                controls={false}
              >
                <source src={classData.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {/* Custom Video Controls - would be replaced with a proper video player in production */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                {/* Progress Bar */}
                <div className="relative mb-2">
                  <input
                    type="range"
                    min="0"
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-1 bg-gray-600 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${
                        (currentTime / (duration || 100)) * 100
                      }%, #4b5563 ${
                        (currentTime / (duration || 100)) * 100
                      }%, #4b5563 100%)`,
                    }}
                  />
                </div>

                {/* Controls Row */}
                <div className="flex items-center justify-between">
                  {/* Left Controls */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={togglePlay}
                      className="text-white hover:text-indigo-300 focus:outline-none"
                    >
                      {isPlaying ? (
                        <svg
                          className="w-8 h-8"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-8 h-8"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>

                    {/* Volume Control */}
                    <div className="relative flex items-center">
                      <button
                        className="text-white hover:text-indigo-300 focus:outline-none"
                        onClick={() => setShowVolumeControl(!showVolumeControl)}
                      >
                        {volume === 0 ? (
                          <svg
                            className="w-6 h-6"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 101.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0014 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" />
                          </svg>
                        ) : volume < 0.5 ? (
                          <svg
                            className="w-6 h-6"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M7 9v6h4l5 5V4l-5 5H7z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-6 h-6"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                          </svg>
                        )}
                      </button>
                      {showVolumeControl && (
                        <div className="absolute bottom-full left-0 mb-2 bg-gray-800 rounded-lg p-2 w-32">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>

                    {/* Time Display */}
                    <div className="text-white text-sm">
                      {formatTime(currentTime)} / {formatTime(duration || 0)}
                    </div>
                  </div>

                  {/* Right Controls */}
                  <div className="flex items-center space-x-3">
                    {/* Playback Speed */}
                    <div className="relative">
                      <button
                        className="text-white hover:text-indigo-300 text-sm font-medium flex items-center focus:outline-none"
                        onClick={() =>
                          setShowPlaybackOptions(!showPlaybackOptions)
                        }
                      >
                        <span>{playbackRate}x</span>
                        <svg
                          className="w-4 h-4 ml-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {showPlaybackOptions && (
                        <div className="absolute bottom-full right-0 mb-2 bg-gray-800 rounded-lg overflow-hidden">
                          {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                            <button
                              key={rate}
                              className={`block w-full text-left px-4 py-2 text-sm ${
                                playbackRate === rate
                                  ? "bg-indigo-600 text-white"
                                  : "text-white hover:bg-gray-700"
                              }`}
                              onClick={() => handlePlaybackRateChange(rate)}
                            >
                              {rate}x
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Fullscreen Toggle */}
                    <button
                      className="text-white hover:text-indigo-300 focus:outline-none"
                      onClick={() => {
                        if (videoRef.current) {
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          } else {
                            videoRef.current.requestFullscreen();
                          }
                        }
                      }}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                      </svg>
                    </button>

                    {/* Toggle Notes on mobile */}
                    <button
                      className="md:hidden text-white hover:text-indigo-300 focus:outline-none"
                      onClick={() => setShowNotes(!showNotes)}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Info and Progress */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-2 md:mb-0">
                  {classData.title}
                </h1>
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-xs font-medium px-2.5 py-0.5 rounded ${
                      classData.type === "foundation"
                        ? "bg-green-100 text-green-800"
                        : classData.type === "prelims"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {classData.type.charAt(0).toUpperCase() +
                      classData.type.slice(1)}
                  </span>
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">
                    {classData.subject}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-600 mb-6">
                <div className="flex items-center mr-6 mb-2 sm:mb-0">
                  <svg
                    className="w-4 h-4 mr-1 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  {classData.instructor}
                </div>
                <div className="flex items-center mr-6 mb-2 sm:mb-0">
                  <svg
                    className="w-4 h-4 mr-1 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {classData.duration}
                </div>
                <div className="flex items-center mr-6 mb-2 sm:mb-0">
                  <svg
                    className="w-4 h-4 mr-1 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  {classData.views} views
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {classData.rating} rating
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Your progress
                  </span>
                  <span className="text-sm text-gray-600">
                    {classData.completion}% completed
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full"
                    style={{ width: `${classData.completion}%` }}
                  ></div>
                </div>
              </div>

              <p className="text-gray-700 mb-6">{classData.description}</p>

              <div className="flex flex-wrap gap-2">
                <button className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded hover:bg-indigo-200 transition-colors duration-300">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                  Add to Bookmarks
                </button>
                <button className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded hover:bg-indigo-200 transition-colors duration-300">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  Share
                </button>
                <button className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded hover:bg-indigo-200 transition-colors duration-300">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                    />
                  </svg>
                  Leave Feedback
                </button>
                <button className="inline-flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded hover:bg-indigo-200 transition-colors duration-300">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Resources
                </button>
              </div>
            </div>

            {/* Chapters Navigation (Mobile only) */}
            <div className="lg:hidden bg-white rounded-lg shadow-md p-4 mt-4">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Chapters</h2>
              <div className="space-y-2">
                {classData.chapters.map((chapter, index) => (
                  <button
                    key={index}
                    className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-md transition-colors duration-300"
                    onClick={() => seekToTimestamp(chapter.timestamp)}
                  >
                    <div className="flex items-center">
                      <span className="w-6 h-6 text-xs flex items-center justify-center bg-indigo-100 text-indigo-800 rounded-full mr-2">
                        {index + 1}
                      </span>
                      <span className="text-gray-800">{chapter.title}</span>
                    </div>
                    <span className="text-gray-500 text-sm">
                      {chapter.timestamp}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Related Classes (Mobile only) */}
            <div className="lg:hidden bg-white rounded-lg shadow-md p-4 mt-4">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                Related Classes
              </h2>
              <div className="space-y-4">
                {relatedClasses.map((relClass) => (
                  <Link
                    key={relClass.id}
                    to={`/watch-class/${relClass.id}`}
                    className="flex items-start hover:bg-gray-50 p-2 rounded-md transition-colors duration-300"
                  >
                    <div className="flex-shrink-0 relative w-20 h-12 rounded overflow-hidden mr-3">
                      <img
                        src={relClass.image}
                        alt={relClass.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 right-0 m-1">
                        <span className="bg-black/60 text-white text-xs px-1 py-0.5 rounded">
                          {relClass.duration}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {relClass.title}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {relClass.instructor}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Notes and Resources Sidebar */}
          {showNotes && (
            <div className="lg:w-2/5 lg:pl-6 mt-6 lg:mt-0">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b">
                  <button
                    className={`flex-1 py-3 text-sm font-medium ${
                      activeTab === "notes"
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab("notes")}
                  >
                    Notes & Transcript
                  </button>
                  <button
                    className={`flex-1 py-3 text-sm font-medium ${
                      activeTab === "chapters"
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab("chapters")}
                  >
                    Chapters
                  </button>
                  <button
                    className={`flex-1 py-3 text-sm font-medium ${
                      activeTab === "resources"
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab("resources")}
                  >
                    Resources
                  </button>
                  <button
                    className={`flex-1 py-3 text-sm font-medium ${
                      activeTab === "quiz"
                        ? "text-indigo-600 border-b-2 border-indigo-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    onClick={() => setActiveTab("quiz")}
                  >
                    Quiz
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-4 h-[600px] overflow-y-auto">
                  {/* Notes & Transcript Tab */}
                  {activeTab === "notes" && (
                    <div className="space-y-6">
                      {classData.notes.map((note, index) => (
                        <div
                          key={index}
                          className="border-b border-gray-200 pb-5 last:border-b-0 last:pb-0"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="text-lg font-medium text-gray-900">
                              {note.title}
                            </h3>
                            <button
                              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                              onClick={() => seekToTimestamp(note.timestamp)}
                            >
                              {note.timestamp}
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm whitespace-pre-line">
                            {note.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chapters Tab */}
                  {activeTab === "chapters" && (
                    <div className="space-y-3">
                      {classData.chapters.map((chapter, index) => (
                        <button
                          key={index}
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-md transition-colors duration-300"
                          onClick={() => seekToTimestamp(chapter.timestamp)}
                        >
                          <div className="flex items-center">
                            <span className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-800 rounded-full mr-3">
                              {index + 1}
                            </span>
                            <span className="text-gray-800 font-medium">
                              {chapter.title}
                            </span>
                          </div>
                          <span className="text-gray-500">
                            {chapter.timestamp}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Resources Tab */}
                  {activeTab === "resources" && (
                    <div className="space-y-4">
                      {classData.resources.map((resource, index) => (
                        <a
                          key={index}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-300"
                        >
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-3 bg-indigo-100 text-indigo-600">
                            {resource.type === "pdf" ? (
                              <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                                <path d="M3 8a2 2 0 012-2h2.5a1 1 0 010 2H5v4.5a1 1 0 01-2 0V8z" />
                              </svg>
                            ) : resource.type === "image" ? (
                              <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-6 h-6"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">
                              {resource.name}
                            </h4>
                            <div className="flex items-center mt-1">
                              <span className="text-xs text-gray-500 mr-3">
                                {resource.type.toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500">
                                {resource.size}
                              </span>
                            </div>
                          </div>
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Quiz Tab */}
                  {activeTab === "quiz" && (
                    <div>
                      <p className="text-gray-700 mb-4">
                        Test your understanding of this class with the following
                        questions:
                      </p>
                      <div className="space-y-6">
                        {classData.quiz.map((question, qIndex) => (
                          <div
                            key={qIndex}
                            className="border border-gray-200 rounded-lg p-4"
                          >
                            <h3 className="text-base font-medium text-gray-900 mb-3">
                              {qIndex + 1}. {question.question}
                            </h3>
                            <div className="space-y-2">
                              {question.options.map((option, oIndex) => (
                                <div key={oIndex} className="flex items-center">
                                  <input
                                    type="radio"
                                    id={`q${qIndex}-o${oIndex}`}
                                    name={`question-${qIndex}`}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                  />
                                  <label
                                    htmlFor={`q${qIndex}-o${oIndex}`}
                                    className="ml-2 text-sm text-gray-700"
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 flex justify-center">
                        <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md transition-colors duration-300">
                          Check Answers
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bookmarks Section */}
              <div className="bg-white rounded-lg shadow-md p-4 mt-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">
                  Your Bookmarks
                </h2>
                {classData.bookmarks.length > 0 ? (
                  <div className="space-y-2">
                    {classData.bookmarks.map((bookmark, index) => (
                      <button
                        key={index}
                        className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-md transition-colors duration-300"
                        onClick={() => seekToTimestamp(bookmark.time)}
                      >
                        <div className="flex items-center">
                          <svg
                            className="w-4 h-4 text-indigo-600 mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                          </svg>
                          <span className="text-gray-800 text-sm">
                            {bookmark.label}
                          </span>
                        </div>
                        <span className="text-gray-500 text-sm">
                          {bookmark.time}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm">
                    No bookmarks yet. Add bookmarks while watching to quickly
                    jump to important points.
                  </p>
                )}
                <button className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add Bookmark at Current Time
                </button>
              </div>

              {/* Related Classes */}
              <div className="hidden lg:block bg-white rounded-lg shadow-md p-4 mt-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">
                  Related Classes
                </h2>
                <div className="space-y-4">
                  {relatedClasses.map((relClass) => (
                    <Link
                      key={relClass.id}
                      to={`/watch-class/${relClass.id}`}
                      className="flex items-start hover:bg-gray-50 p-2 rounded-md transition-colors duration-300"
                    >
                      <div className="flex-shrink-0 relative w-24 h-16 rounded overflow-hidden mr-3">
                        <img
                          src={relClass.image}
                          alt={relClass.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 right-0 m-1">
                          <span className="bg-black/60 text-white text-xs px-1 py-0.5 rounded">
                            {relClass.duration}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                          {relClass.title}
                        </h3>
                        <p className="text-xs text-gray-600">
                          {relClass.instructor}
                        </p>
                        <div className="mt-1 flex items-center">
                          <span
                            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                              relClass.type === "foundation"
                                ? "bg-green-100 text-green-800"
                                : relClass.type === "prelims"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {relClass.type.charAt(0).toUpperCase() +
                              relClass.type.slice(1)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/recorded-classes"
                  className="mt-4 text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center justify-center"
                >
                  <span>Browse All Classes</span>
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Navigation (Prev/Next Classes) */}
      <div className="bg-white border-t border-gray-200 py-4 sticky bottom-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between">
            <Link
              to="/watch-class/previous-class-id"
              className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors duration-300"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="text-sm font-medium">Previous Class</span>
            </Link>
            <Link
              to="/recorded-classes"
              className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors duration-300"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
              <span className="text-sm font-medium">All Classes</span>
            </Link>
            <Link
              to="/watch-class/next-class-id"
              className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors duration-300"
            >
              <span className="text-sm font-medium">Next Class</span>
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchClassPage;
